/**
 * Login page (PR-10 + PR-09 + PR-08 + PR-13).
 *
 * Rendered by App.tsx when /api/me returns 401. Three sign-in paths:
 *
 *   1. GitHub OAuth (PR-07) — single anchor `<a href="/api/auth/github/start">`
 *      so the browser does a top-level navigation: the CSRF state cookie
 *      sticks and the user can verify they're heading to github.com in
 *      the address bar.
 *   2. Google OAuth (PR-08) — same anchor pattern, distinct CSRF cookie
 *      (`draftlize_oauth_state_google`) on the server side so the two
 *      OAuth flows can race without colliding.
 *   3. Email magic-link (PR-09) — POST {email} to /api/auth/email/request,
 *      flip the card to "check your email" once the request resolves.
 *      Server always returns 200 (don't leak whether the email is
 *      registered), so we treat any 200 as success.
 *      PR-13 added an hCaptcha gate on this endpoint when the server is
 *      configured with hCaptcha keys. The OAuth flows skip captcha (they
 *      have their own anti-abuse). The widget loads + renders only after
 *      `captchaConfig()` reports `enabled: true`, so a fresh dev machine
 *      without keys still sees the simple form.
 *
 * Visual: warm-paper aesthetic from the rest of the app — Fraunces for
 * the wordmark / tagline, Inter for the buttons. Centered single card
 * on the same `--paper-0` background body uses, no gradient or visual
 * noise.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from './LanguageToggle';
import { api } from '../api';

// Window-attached callback the hCaptcha script invokes when the user
// solves the challenge. Declared on `window` (not via React) because the
// hCaptcha script reads `data-callback="onHcaptchaSolved"` and looks the
// name up via `window[name]` — there's no React-aware path. We attach +
// detach inside an effect so the latest setter closure is always live.
declare global {
  interface Window {
    onHcaptchaSolved?: (token: string) => void;
    onHcaptchaExpired?: () => void;
  }
}

type FormState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; to: string }
  | { kind: 'error'; message: string };

type WaitlistState =
  | { kind: 'hidden' }
  | { kind: 'form' }
  | { kind: 'sending' }
  | { kind: 'joined' };

/** WeChat's in-app browser blocks/mangles third-party OAuth redirects
 *  (Google entirely, GitHub unreliably). Detecting it lets us show only
 *  the path that actually works there: email magic-link. doc/12 §3.4.2. */
function isWeChat(): boolean {
  return typeof navigator !== 'undefined'
    && /MicroMessenger/i.test(navigator.userAgent);
}

/** Normalize invite input the same way the server does (it re-checks):
 *  uppercase, strip whitespace + dashes people paste from chat. */
function normalizeInvite(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase();
}

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<FormState>({ kind: 'idle' });
  // PR-13: hCaptcha config is fetched on mount. `null` = haven't asked
  // yet; once we know, we render the widget conditionally on enabled.
  const [captchaCfg, setCaptchaCfg] = useState<
    { enabled: boolean; site_key: string | null } | null
  >(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const widgetMountRef = useRef<HTMLDivElement | null>(null);
  // doc/12 §3.1: invite gate. `null` = probe in flight; while unknown we
  // render the form without the invite field (existing users sign in
  // unaffected either way — the gate only fires on net-new signups).
  const [inviteGate, setInviteGate] = useState<boolean | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [waitlist, setWaitlist] = useState<WaitlistState>({ kind: 'hidden' });
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const wechat = isWeChat();

  // Probe the server for hCaptcha config. Failing here is non-fatal —
  // we treat unreachable as "captcha disabled" so the form still works
  // (the server will then either accept the request or reject it; either
  // way the user has a path forward via OAuth).
  useEffect(() => {
    api.captchaConfig()
      .then(setCaptchaCfg)
      .catch(() => setCaptchaCfg({ enabled: false, site_key: null }));
    api.inviteConfig()
      .then((c) => setInviteGate(c.gate))
      .catch(() => setInviteGate(false));
  }, []);

  // OAuth callbacks bounce invite rejections back here as
  // /login?error=invite_required|invite_invalid (they're browser
  // redirects, not fetches). Surface them once, then clean the URL so a
  // reload doesn't re-show a stale error.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err === 'invite_required' || err === 'invite_invalid') {
      setInviteError(err);
      params.delete('error');
      const qs = params.toString();
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (qs ? `?${qs}` : ''),
      );
    }
  }, []);

  // Persist the code for the OAuth round-trip: a short-lived JS cookie
  // the server callback reads on the net-new branch. Written on every
  // keystroke (cheap) so clicking an OAuth anchor needs no interception.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const v = normalizeInvite(inviteCode);
    if (v) {
      document.cookie = `draftlize_invite=${v}; Path=/; Max-Age=600; SameSite=Lax`;
    } else {
      document.cookie = 'draftlize_invite=; Path=/; Max-Age=0; SameSite=Lax';
    }
  }, [inviteCode]);

  const onJoinWaitlist = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = waitlistEmail.trim();
    if (!trimmed) return;
    setWaitlist({ kind: 'sending' });
    try {
      const locale =
        typeof navigator !== 'undefined' ? navigator.language : undefined;
      await api.joinWaitlist(trimmed, locale);
      setWaitlist({ kind: 'joined' });
    } catch {
      setWaitlist({ kind: 'form' });
    }
  };

  // doc/07 PR-31: post-login return-URL handoff. Marketing CTAs that
  // bounce unauthed users through /login pass `?return=<path>` so the
  // user can resume their original intent (typically /checkout?tier=X).
  // We stash the return path in localStorage; App.tsx replays it on the
  // auth-in transition. Same-origin paths only — guard against
  // open-redirect by rejecting anything that doesn't start with `/` or
  // that starts with `//` (protocol-relative URL).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ret = new URLSearchParams(window.location.search).get('return');
    if (ret && ret.startsWith('/') && !ret.startsWith('//')) {
      try {
        localStorage.setItem('draftlize.post_login_return', ret);
      } catch {
        // Private browsing / quota-full — fall through; user just won't
        // get auto-resumed, they can re-click the CTA after login.
      }
    }
  }, []);

  // Inject the hCaptcha script + wire the global callbacks. Only fires
  // once we know captcha is enabled, so a server without keys never
  // pulls in 50 KB from hcaptcha.com. The script auto-renders any
  // `.h-captcha` div it finds when loaded with `?render=onload`.
  useEffect(() => {
    if (!captchaCfg?.enabled) return;
    window.onHcaptchaSolved = (token: string) => setCaptchaToken(token);
    // Token expires after ~2min idle; widget calls back so we re-disable
    // the submit until the user solves again.
    window.onHcaptchaExpired = () => setCaptchaToken(null);
    if (!document.querySelector('script[src*="hcaptcha.com"]')) {
      const s = document.createElement('script');
      s.src = 'https://js.hcaptcha.com/1/api.js?render=onload';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    return () => {
      // Avoid leaking the closure-captured setter if the page unmounts
      // (Vite HMR + dev re-renders in particular).
      delete window.onHcaptchaSolved;
      delete window.onHcaptchaExpired;
    };
  }, [captchaCfg?.enabled]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    // When captcha is enabled, gate submit on a solved token. Disabled
    // submit button below also enforces this — belt + braces.
    if (captchaCfg?.enabled && !captchaToken) return;
    setState({ kind: 'sending' });
    setInviteError(null);
    try {
      await api.requestMagicLink(
        trimmed,
        captchaToken,
        inviteGate ? normalizeInvite(inviteCode) : null,
      );
      setState({ kind: 'sent', to: trimmed });
    } catch (err) {
      const kind = (err as Error & { inviteKind?: string }).inviteKind;
      if (kind === 'invite_required' || kind === 'invite_invalid') {
        setInviteError(kind);
        setState({ kind: 'idle' });
      } else {
        setState({
          kind: 'error',
          message: t('login.errorSendFailed'),
        });
      }
      // Reset the captcha so the user can retry — a used (or failed)
      // token is dead either way.
      setCaptchaToken(null);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--paper-0)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
    >
      <div style={{ position: 'fixed', top: 16, right: 16 }}>
        <LanguageToggle />
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          background: 'white',
          border: '1px solid var(--rule)',
          borderRadius: 10,
          padding: '36px 32px 28px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.02), 0 8px 24px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Wordmark — same `D` block as the sidebar header so the
            login page reads as the same product, just stripped down. */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: 'var(--ink-0)',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 14,
          }}
        >
          D
        </div>
        <div
          className="serif"
          style={{
            fontSize: 22,
            fontWeight: 500,
            color: 'var(--ink-0)',
            letterSpacing: '-0.01em',
            marginBottom: 4,
          }}
        >
          Draftlize
        </div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.1em',
            marginBottom: 28,
            textTransform: 'uppercase',
          }}
        >
          {t('login.tagline')}
        </div>

        {state.kind === 'sent' ? (
          <SentConfirmation
            to={state.to}
            onReset={() => {
              setState({ kind: 'idle' });
              setEmail('');
            }}
          />
        ) : waitlist.kind !== 'hidden' ? (
          <WaitlistView
            state={waitlist}
            email={waitlistEmail}
            onEmailChange={setWaitlistEmail}
            onSubmit={onJoinWaitlist}
            onBack={() => setWaitlist({ kind: 'hidden' })}
          />
        ) : (
          <>
            {/* doc/12 §3.1: invite gate. One field feeds all three
                sign-in paths — the email form posts it, the OAuth
                anchors carry it via the draftlize_invite cookie the
                effect above maintains. */}
            {inviteGate && (
              <div style={{ width: '100%', marginBottom: 14 }}>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => {
                    setInviteCode(e.target.value);
                    if (inviteError) setInviteError(null);
                  }}
                  placeholder={t('login.invitePlaceholder')}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  className="mono"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 13,
                    letterSpacing: '.12em',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    color: 'var(--ink-0)',
                    background: 'white',
                    border: `1px solid ${inviteError ? '#c66' : 'var(--rule)'}`,
                    borderRadius: 6,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 120ms ease',
                  }}
                  onFocus={(e) => {
                    if (!inviteError) e.currentTarget.style.borderColor = 'var(--ink-2)';
                  }}
                  onBlur={(e) => {
                    if (!inviteError) e.currentTarget.style.borderColor = 'var(--rule)';
                  }}
                />
                {inviteError && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: '#a33',
                      textAlign: 'center',
                    }}
                  >
                    {inviteError === 'invite_invalid'
                      ? t('login.inviteInvalid')
                      : t('login.inviteRequired')}
                  </div>
                )}
              </div>
            )}

            {/* doc/12 §3.4.2: WeChat's webview breaks third-party OAuth —
                Google entirely, GitHub unreliably. Inside WeChat we show
                only the email path plus a short note. */}
            {wechat && (
              <div
                style={{
                  width: '100%',
                  marginBottom: 4,
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: 'var(--ink-2)',
                  textAlign: 'center',
                }}
              >
                {t('login.wechatHint')}
              </div>
            )}

            {!wechat && (
            <>
            {/* GitHub button — plain anchor, not a fetch/POST.
                /api/auth/github/start sets a CSRF cookie and 302s — that
                needs to be a top-level navigation so the cookie sticks
                and the GitHub redirect URL is visible in the address bar
                (so users can verify they're going to github.com). */}
            <a
              href="/api/auth/github/start"
              className="github-signin-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                width: '100%',
                padding: '11px 14px',
                background: 'var(--ink-0)',
                color: 'white',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'background 120ms ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  'var(--ink-1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  'var(--ink-0)';
              }}
            >
              <GitHubGlyph />
              <span>{t('login.signInWithGitHub')}</span>
            </a>

            {/* Google button — same anchor pattern as GitHub above; the
                top-level navigation is required so the CSRF state cookie
                set by /api/auth/google/start sticks and Google's domain
                is visible in the address bar. Visually it's the inverse
                of the GitHub button (white / 1px rule) so the colored G
                glyph carries the brand without the whole button being
                colored. */}
            <a
              href="/api/auth/google/start"
              className="google-signin-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                width: '100%',
                padding: '11px 14px',
                marginTop: 8,
                background: 'white',
                color: 'var(--ink-0)',
                border: '1px solid var(--rule)',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  'var(--paper-1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'white';
              }}
            >
              <GoogleGlyph />
              <span>{t('login.signInWithGoogle')}</span>
            </a>

            <Separator label={t('login.separatorOr')} />
            </>
            )}

            <form
              onSubmit={onSubmit}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                width: '100%',
              }}
            >
              <input
                type="email"
                required
                disabled={state.kind === 'sending'}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Reset error state once the user starts editing.
                  if (state.kind === 'error') setState({ kind: 'idle' });
                }}
                placeholder={t('login.emailPlaceholder')}
                autoComplete="email"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  color: 'var(--ink-0)',
                  background: 'white',
                  border: '1px solid var(--rule)',
                  borderRadius: 6,
                  outline: 'none',
                  transition: 'border-color 120ms ease',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--ink-2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--rule)';
                }}
              />
              {/* PR-13: hCaptcha widget. Renders only when the server
                  reports captcha enabled. The hCaptcha script (loaded in
                  the effect above with `?render=onload`) auto-renders
                  any `.h-captcha` div on the page, so we just emit the
                  div with the right data-* attrs. The `data-callback`
                  global is wired up in the effect to setCaptchaToken. */}
              {captchaCfg?.enabled && captchaCfg.site_key && (
                <div
                  ref={widgetMountRef}
                  className="h-captcha"
                  data-sitekey={captchaCfg.site_key}
                  data-callback="onHcaptchaSolved"
                  data-expired-callback="onHcaptchaExpired"
                  style={{ alignSelf: 'center' }}
                />
              )}
              <button
                type="submit"
                disabled={
                  state.kind === 'sending'
                  || !email.trim()
                  || (captchaCfg?.enabled === true && !captchaToken)
                }
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'white',
                  color: 'var(--ink-0)',
                  border: '1px solid var(--rule)',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  cursor:
                    state.kind === 'sending'
                    || !email.trim()
                    || (captchaCfg?.enabled === true && !captchaToken)
                      ? 'default'
                      : 'pointer',
                  opacity:
                    state.kind === 'sending'
                    || !email.trim()
                    || (captchaCfg?.enabled === true && !captchaToken)
                      ? 0.55
                      : 1,
                  transition: 'background 120ms ease, border-color 120ms ease',
                }}
                onMouseEnter={(e) => {
                  if (
                    state.kind !== 'sending'
                    && email.trim()
                    && (!captchaCfg?.enabled || captchaToken)
                  ) {
                    e.currentTarget.style.background = 'var(--paper-1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                {state.kind === 'sending'
                  ? t('login.sending')
                  : t('login.sendMagicLink')}
              </button>
            </form>

            {state.kind === 'error' && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: '#a33',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {state.message}
              </div>
            )}

            {/* doc/12 §3.1.2: code-less visitors leave an email instead
                of bouncing. Only shown while the gate is on. */}
            {inviteGate && (
              <button
                type="button"
                onClick={() => setWaitlist({ kind: 'form' })}
                style={{
                  marginTop: 16,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--ink-2)',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                {t('login.noInviteWaitlist')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function WaitlistView({
  state,
  email,
  onEmailChange,
  onSubmit,
  onBack,
}: {
  state: WaitlistState;
  email: string;
  onEmailChange: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  if (state.kind === 'joined') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          width: '100%',
        }}
      >
        <div
          className="serif"
          style={{
            fontSize: 15,
            color: 'var(--ink-0)',
            textAlign: 'center',
            lineHeight: 1.45,
          }}
        >
          {t('login.waitlistJoinedTitle')}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-2)',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          {t('login.waitlistJoinedBody')}
        </div>
      </div>
    );
  }
  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}
    >
      <div
        style={{
          fontSize: 13,
          color: 'var(--ink-1)',
          textAlign: 'center',
          lineHeight: 1.5,
          marginBottom: 4,
        }}
      >
        {t('login.waitlistIntro')}
      </div>
      <input
        type="email"
        required
        disabled={state.kind === 'sending'}
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        placeholder={t('login.emailPlaceholder')}
        autoComplete="email"
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 13,
          fontFamily: 'inherit',
          color: 'var(--ink-0)',
          background: 'white',
          border: '1px solid var(--rule)',
          borderRadius: 6,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <button
        type="submit"
        disabled={state.kind === 'sending' || !email.trim()}
        style={{
          width: '100%',
          padding: '10px 14px',
          background: 'var(--ink-0)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'inherit',
          cursor: state.kind === 'sending' || !email.trim() ? 'default' : 'pointer',
          opacity: state.kind === 'sending' || !email.trim() ? 0.55 : 1,
        }}
      >
        {state.kind === 'sending'
          ? t('login.sending')
          : t('login.waitlistJoin')}
      </button>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-2)',
          fontSize: 12,
          fontFamily: 'inherit',
          cursor: 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        }}
      >
        {t('login.waitlistBack')}
      </button>
    </form>
  );
}

function SentConfirmation({
  to,
  onReset,
}: {
  to: string;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  // doc/12 §3.4.2: the email carries both a link and a 6-digit code.
  // Typing the code here is the WeChat-reliable path (email links get
  // blocked in its webview); on success the session cookie is set and
  // we reload into the app shell.
  const [code, setCode] = useState('');
  const [codeState, setCodeState] = useState<'idle' | 'busy' | 'bad'>('idle');
  const digits = code.replace(/\D/g, '');
  const submitCode = async () => {
    if (digits.length !== 6 || codeState === 'busy') return;
    setCodeState('busy');
    try {
      await api.verifyEmailCode(to, digits);
      window.location.replace('/');
    } catch {
      setCodeState('bad');
    }
  };
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        width: '100%',
      }}
    >
      <div
        className="serif"
        style={{
          fontSize: 15,
          color: 'var(--ink-0)',
          textAlign: 'center',
          lineHeight: 1.45,
        }}
      >
        {t('login.sentTitle')}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--ink-2)',
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        {t('login.sentBodyPrefix')}{' '}
        <span style={{ color: 'var(--ink-0)', fontWeight: 500 }}>{to}</span>
        {t('login.sentBodySuffix')}
      </div>

      <div
        style={{
          width: '100%',
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--ink-2)', textAlign: 'center' }}>
          {t('login.codeHint')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, ''));
              if (codeState === 'bad') setCodeState('idle');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitCode();
            }}
            placeholder="000000"
            className="mono"
            style={{
              flex: 1,
              padding: '10px 12px',
              fontSize: 16,
              letterSpacing: '.3em',
              textAlign: 'center',
              color: 'var(--ink-0)',
              background: 'white',
              border: `1px solid ${codeState === 'bad' ? '#c66' : 'var(--rule)'}`,
              borderRadius: 6,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="button"
            disabled={digits.length !== 6 || codeState === 'busy'}
            onClick={() => void submitCode()}
            style={{
              padding: '10px 16px',
              background: 'var(--ink-0)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor:
                digits.length !== 6 || codeState === 'busy' ? 'default' : 'pointer',
              opacity: digits.length !== 6 || codeState === 'busy' ? 0.55 : 1,
            }}
          >
            {codeState === 'busy' ? '…' : t('login.codeSubmit')}
          </button>
        </div>
        {codeState === 'bad' && (
          <div style={{ fontSize: 12, color: '#a33', textAlign: 'center' }}>
            {t('login.codeInvalid')}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onReset}
        style={{
          marginTop: 8,
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-2)',
          fontSize: 12,
          fontFamily: 'inherit',
          cursor: 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        }}
      >
        {t('login.useDifferentEmail')}
      </button>
    </div>
  );
}

function Separator({ label }: { label: string }) {
  return (
    <div
      className="mono"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        margin: '16px 0',
        fontSize: 10,
        color: 'var(--ink-3)',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
      }}
    >
      <div style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
      <span>{label}</span>
      <div style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
    </div>
  );
}

/** Inline Google "G" mark — minimal four-color version. Distinct
 *  fills (not currentColor) keep the brand recognizable on the white
 *  button. Path data is the public Google identity G simplified to
 *  the four shapes that make it unambiguous. */
function GoogleGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Inline GitHub mark — avoids a runtime asset round-trip. The path is the
 *  official Octocat logo simplified to one path; the `currentColor` fill
 *  inherits the button's white text color. */
function GitHubGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56 0-.27-.01-1.18-.02-2.14-3.2.69-3.87-1.36-3.87-1.36-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.68 1.24 3.34.95.1-.74.4-1.24.73-1.53-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.04 0 0 .96-.31 3.15 1.18.91-.25 1.89-.38 2.86-.39.97.01 1.95.14 2.86.39 2.18-1.49 3.14-1.18 3.14-1.18.62 1.58.23 2.75.11 3.04.74.81 1.18 1.84 1.18 3.1 0 4.42-2.69 5.39-5.25 5.68.41.36.78 1.07.78 2.16 0 1.56-.01 2.82-.01 3.21 0 .31.21.68.8.56 4.56-1.52 7.85-5.83 7.85-10.91C23.5 5.65 18.35.5 12 .5z" />
    </svg>
  );
}
